/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {editAdminUser} from '@ion/applications/src/pages/admin/users/state';
import {translate} from '@ion/core/src/locale/locale';
import Avatar from '@ion/dls/src/components/avatar/Avatar';
import {CTALink} from '@ion/dls/src/components/buttons/CTALink';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import useToggleToolTip from '@ion/dls/src/hooks/useToggleToolTip';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import {useCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
import {showChangeGroup} from '@/store/actions/global-group-chooser';
import {signoutCTC} from '@/store/actions/login';
import {viewPrivacyPolicy} from '../../header/DesktopUserProfileView';
import {useLogout} from '../../hooks/useLogout';
import GlobalGroupChooserMobile from './GlobalGroupChooserMobile';

export interface UserProfileMobileProps {
  showProfile: boolean;
  handleClose: () => void;
}

const useStyles = makeStyles(theme => ({
  mainContainer : {
    margin       : '40px',
    marginBottom : 0
  },
  subContainer : {
    display      : 'flex',
    width        : '100%',
    borderBottom : `1px solid ${dlsColor('grey4')}`
  },
  user : {
    wordBreak  : 'break-all',
    display    : 'block',
    fontSize   : '16px',
    lineHeight : '20px',
    color      : dlsColor('grey1'),
    fontWeight : 500
  },
  account : {
    lineHeight : '20px',
    fontSize   : '14px',
    color      : dlsColor('grey3'),
    fontWeight : 400
  },
  ionLink : {
    ...dlsAlt('components', 'ionLinkStyle', 'ionLink')
  },
  disabled : {
    ...dlsAlt('components', 'ionLinkStyle', 'ionLink'),
    color : dlsColor('grey4')
  },
  betaLink : {
    backgroundColor : dlsColor('caBlue'),
    borderRadius    : '20px',
    marginLeft      : '25px',
    ...dlsAlt('components', 'ionpreviewText', 'new')
  },
  ionBeta : {
    display    : 'flex',
    alignItems : 'center'
  },
  customTooltip : {
    marginRight : '205px'
  },
  supLink : {
    position : 'relative',
    top      : '0.5em',
    left     : '-0.2em'
  },
  optionWrapper : {
    width          : '100%',
    alignItems     : 'center',
    display        : 'flex',
    justifyContent : 'space-between'
  },
  profileItem : {
    width          : '100%',
    alignItems     : 'center',
    display        : 'flex',
    justifyContent : 'space-between',
    marginTop      : '27px'
  },
  ctaLink : {
    display    : 'flex',
    alignItems : 'center'
  },
  ctaBold : {
    color  : dlsColor('grey2'),
    cursor : 'pointer',
    ...dlsTypography('cta')
  },
  container : {
    display      : 'flex',
    width        : '100%',
    padding      : 8,
    borderBottom : `1px solid ${dlsColor('grey5')}`
  },
  profileTitle : {
    width     : '100%',
    textAlign : 'center',
    marginTop : 6
  },
  userProfileHeader : {
    display       : 'flex',
    textTransform : 'uppercase',
    fontSize      : '20px',
    weight        : 500,
    lineHeight    : '30px',
    width         : '100%',
    alignItems    : 'center',
    verticalAlign : 'middle'
  },
  comingSoon : {
    display    : 'flex',
    alignItems : 'center',
    marginTop  : '27px'
  }
}));

/**
 * @userProfileMobile
 */
export const UserProfileMobile = ({showProfile, handleClose}: UserProfileMobileProps) => {
  const classes                         = useStyles();
  const dispatch                        = useDispatch();
  const isFromLegacyAdmin               = useSelector(({initialization}) => initialization.isFromLegacyAdmin);
  const {isViewing: displayChangeGroup} = useSelector(({globalGroup: {groupChooser}}) => groupChooser);
  const isLoggingOut                    = useSelector(({login}) => login.isAuthenticating);
  const {currentUser}                   = useCurrentUser();
  const accountName                     = currentUser?.account?.title;
  const username                        = currentUser.username;
  const [tooltipId, toggleToolTip]      = useToggleToolTip(null);
  const onLogout                        = useLogout();

  const editProfile = function() {
    dispatch(editAdminUser(currentUser, true, false));
  };

  const toggleChangeGroup = function() {
    dispatch(showChangeGroup(!displayChangeGroup));
  };

  function onClose() {
    handleClose?.();
  }

  return (
    <Dialog fullScreen open={showProfile}>
      <div style={{width: '100%', height: '100%', overflow: 'hidden'}}>
        <div className={classes.container}>
          <div className={classes.userProfileHeader}>
            <IconButton icon={<MaterialExitCloseIcon />}
              style={{position: 'absolute'}}
              onClick={onClose}
              disableRipple />
            <HeaderTypography variant='h2' className={classes.profileTitle}>
              {translate('nav:header.account')}
            </HeaderTypography>
          </div>
        </div>
        <div style={{height: '70%'}}>
          <div className={classes.mainContainer}>
            <div className={classes.subContainer}>
              <div>
                <Avatar name={username} />
              </div>
              <div style={{height: '64px', marginLeft: '24px'}}>
                <label className={classes.user}>{username}</label>
                <label className={classes.account}>{accountName}</label>
              </div>
            </div>
            <div>
              <div className={classes.profileItem}>
                <CTALink className={classes.ctaLink} onClick={toggleChangeGroup}>
                  {translate('common:editors.shared.changeGroup')}
                </CTALink>
                <GlobalGroupChooserMobile toggleChangeGroup={toggleChangeGroup} />
              </div>
              <div className={classes.profileItem}>
                <CTALink className={classes.ctaLink} onClick={editProfile}>
                  {translate('nav:header.profile')}
                </CTALink>
              </div>
              <div className={classes.profileItem}>
                <CTALink className={classnames(classes.ctaLink)}
                  onClick={viewPrivacyPolicy(onClose)}>
                  {translate('nav:header.privacyPolicy')}
                </CTALink>
              </div>
            </div>
          </div>
        </div>
        <div style={{height: '30%', marginLeft: '40px'}}>
          <Render>
            <Render.If condition={!isLoggingOut}>
              <CTALink style={{pointerEvents: isLoggingOut ? 'none' : 'initial'}}
                className={classes.ionLink}
                onClick={onLogout}>
                {translate('nav:header.logout')}
              </CTALink>
            </Render.If>
            <Render.Else>
              <ButtonSpinner />
            </Render.Else>
          </Render>
        </div>
      </div>
    </Dialog>
  );
};
export default UserProfileMobile;
